import CSSPlugin from 'gsap/CSSPlugin';
import TweenLite from 'gsap/TweenLite';
import React, { MutableRefObject, useEffect, useRef } from 'react';
import {
  PolicyRecommendation,
} from '../../graphQL/types/shared';
import { failIfValidOrNonExhaustive, millisecondsPerSeconds } from '../../Utils';
import usePropsChangeRateLimiter, { PropsChangeHandlerInput } from '../sectionNav/usePropsChangeRateLimiter';
import {
  highlightedNodeRadius,
  policyColors,
  regularNodeRadius,
} from './Utils';

// Need to do this so that `CSSPlugin` is not dropped by the minifier:
/* tslint:disable-next-line:no-unused-expression */
CSSPlugin;

interface PropsToSVGStyleInput {
  type: Props['type'];
  isFirstRender: boolean;
}
const propsToSVGStyle = (input: PropsToSVGStyleInput) => {
  const {type, isFirstRender} = input;

  let strokeWidth: number;
  let fillOpacity: number, strokeOpacity: number;
  let pointerEvents: string;
  if (type === Type.Normal) {
    strokeWidth = 0.8;
    fillOpacity = 0.4;
    strokeOpacity = 1;
    pointerEvents = 'auto';
  } else if (type === Type.Highlighted || type === Type.Hovered) {
    // This ensures that the lighter border has the same width as the diameter of the darker interior:
    strokeWidth = (highlightedNodeRadius - regularNodeRadius) * 2;
    fillOpacity = 1;
    strokeOpacity = 0.5;
    pointerEvents = type === Type.Highlighted ? 'none' : 'auto';
  } else {
    failIfValidOrNonExhaustive(type, 'Invalid node type ' + type);
    strokeWidth = 0;
    fillOpacity = 0;
    strokeOpacity = 0;
    pointerEvents = 'auto';
  }
  const r = isFirstRender ? 0 : regularNodeRadius;
  return {
    css: {
      strokeWidth, fillOpacity, strokeOpacity, r, pointerEvents,
    },
  };
};

export enum Type {
  Normal,
  Highlighted,
  Hovered,
}

type MonitoredProps = Pick<Props, 'type' | 'policy'>;
interface PropsChangeHandlerExtraInputs {
  elRef: MutableRefObject<SVGCircleElement | null>;
  isFirstRenderRef: MutableRefObject<boolean>;
  animationDuration: number;
}

const performPropsChange = (input: PropsChangeHandlerInput<PropsChangeHandlerExtraInputs, MonitoredProps>) => {
  const {
    nextValue, done,
    extraInputs: {
      elRef, isFirstRenderRef, animationDuration,
    },
  } = input;

  const {current: el} = elRef;
  const {current: isFirstRender} = isFirstRenderRef;
  if (el !== null) {
    const finalStyle = propsToSVGStyle({
      type: nextValue.type,
      isFirstRender: false,
    });
    if (isFirstRender === true) {
      const initialStyle = propsToSVGStyle({
        type: nextValue.type,
        isFirstRender: true,
      });
      TweenLite.fromTo(el, animationDuration / millisecondsPerSeconds, initialStyle, {
        ...finalStyle,
        onComplete: done,
      });
    } else {
      TweenLite.set(el, {
        ...finalStyle,
        onComplete: done,
      });
    }
  }
};

interface Props {
  id: string;
  x: number;
  y: number;
  type: Type;
  policy: PolicyRecommendation;
  onMouseEnter: (id: string) => void;
  onMouseLeave: () => void;
  // in milliseconds
  animationDuration: number;
}

const Circle = (props: Props) => {
  const {x, y, id, type, policy, animationDuration} = props;
  const elRef = useRef<SVGCircleElement | null>(null);
  const isFirstRenderRef = useRef<boolean>(true);

  usePropsChangeRateLimiter({
    value: {type, policy},
    getExtraInputToPropsChangeHandler: () => ({elRef, isFirstRenderRef, animationDuration}),
    performPropsChange,
  });

  useEffect(() => {
    isFirstRenderRef.current = false;
  }, []);

  const onMouseEnter = () => props.onMouseEnter(id);
  const onMouseLeave = () => props.onMouseLeave();

  return (
    <circle
      ref={elRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      cx={x}
      cy={y}
      fill={policyColors[policy]}
      stroke={policyColors[policy]}
      // r must be set within the element itself to work on Firefox
      r={regularNodeRadius}
    />
  );
};

export default Circle;
